import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { doc, onSnapshot } from 'firebase/firestore';
import { IconChevronLeft, IconChevronRight, IconListDetails, IconX } from '@tabler/icons-react';

import type { Recipe } from '@recipe-rack/shared';

import { IconButton, Loader } from '../../design/components';
import { auth, db } from '../Recipe/Firebase/InitialiseFirebase';
import IngredientDrawer from './IngredientDrawer';

/**
 * Minimal subset of the Screen Wake Lock API. Defined locally so we can
 * use it without leaning on lib.dom version skew across CI.
 */
interface WakeLockSentinelLike {
  release: () => Promise<void>;
}

/**
 * Full-screen cook-mode walkthrough at /cook/:recipeId.
 *
 * - Subscribes to the recipe doc so live edits in another tab show up.
 * - Big two-zone tap surface at the bottom (Previous / Next). No swipes,
 *   per design — swipes don't work with greasy fingers.
 * - Holds a screen wake-lock while open on browsers that support it.
 */
const CookMode: React.FC = () => {
  const { recipeId } = useParams<{ recipeId: string }>();
  const navigate = useNavigate();

  const [userId, setUserId] = useState<string | null>(auth.currentUser?.uid ?? null);
  const [authReady, setAuthReady] = useState<boolean>(auth.currentUser !== null);
  const [recipe, setRecipe] = useState<Recipe | null>(null);
  const [recipeLoaded, setRecipeLoaded] = useState(false);
  const [step, setStep] = useState(0);
  const [ingredientsOpen, setIngredientsOpen] = useState(false);

  const wakeLockRef = useRef<WakeLockSentinelLike | null>(null);

  /* ---------- Auth gate ---------- */
  useEffect(() => {
    const unsub = auth.onAuthStateChanged((u) => {
      setUserId(u?.uid ?? null);
      setAuthReady(true);
    });
    return unsub;
  }, []);

  useEffect(() => {
    if (authReady && !userId) {
      navigate('/', { replace: true });
    }
  }, [authReady, userId, navigate]);

  /* ---------- Recipe subscription ---------- */
  useEffect(() => {
    if (!userId || !recipeId) return;
    const ref = doc(db, `users/${userId}/recipes/${recipeId}`);
    const unsub = onSnapshot(ref, (snap) => {
      setRecipeLoaded(true);
      if (!snap.exists()) {
        setRecipe(null);
        return;
      }
      setRecipe({ ...(snap.data() as Recipe), recipeId: snap.id });
    });
    return unsub;
  }, [userId, recipeId]);

  /* ---------- Wake lock ---------- */
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const wl = (navigator as unknown as {
          wakeLock?: { request: (type: string) => Promise<WakeLockSentinelLike> };
        }).wakeLock;
        if (!wl) return; // silent no-op
        const sentinel = await wl.request('screen');
        if (cancelled) {
          sentinel.release().catch(() => undefined);
        } else {
          wakeLockRef.current = sentinel;
        }
      } catch {
        // Some browsers throw on insecure contexts / hidden tabs — silent.
      }
    })();
    return () => {
      cancelled = true;
      const sentinel = wakeLockRef.current;
      wakeLockRef.current = null;
      if (sentinel) {
        sentinel.release().catch(() => undefined);
      }
    };
  }, []);

  /* ---------- Loading & error states ---------- */
  if (!authReady) return null;

  if (!recipeLoaded) {
    return (
      <div className="rr-cook rr-cook-centered">
        <Loader block label="Loading recipe…" />
      </div>
    );
  }

  if (!recipe) {
    return (
      <div className="rr-cook rr-cook-centered">
        <div className="rr-cook-empty">
          <h1 className="rr-display" style={{ fontSize: 32, margin: 0 }}>
            Recipe not found
          </h1>
          <p style={{ color: 'var(--ink-mute)', marginTop: 8 }}>
            We couldn't find a recipe at that id.
          </p>
          <Link to="/" className="rr-cook-link">
            Back to your library
          </Link>
        </div>
      </div>
    );
  }

  if (recipe.version === 0) {
    return (
      <div className="rr-cook rr-cook-centered">
        <Loader block label="This recipe is still being prepared…" />
        <Link to="/" className="rr-cook-link">
          Back to your library
        </Link>
      </div>
    );
  }

  const total = recipe.method.length;
  const safeStep = Math.min(step, Math.max(0, total - 1));
  const currentText = total > 0 ? recipe.method[safeStep] : '';
  const isFirst = safeStep === 0;
  const isLast = safeStep === total - 1 || total === 0;

  const goNext = () => {
    if (!isLast) setStep((s) => Math.min(total - 1, s + 1));
  };
  const goPrev = () => {
    if (!isFirst) setStep((s) => Math.max(0, s - 1));
  };

  return (
    <div className="rr-cook" role="application" aria-label="Cook mode">
      <header className="rr-cook-header">
        <div className="rr-cook-header-left">
          <IconButton
            icon={<IconX size={18} />}
            aria-label="Exit cook mode"
            onClick={() => navigate('/')}
          />
          <IconButton
            icon={<IconListDetails size={18} />}
            aria-label="Show ingredients"
            onClick={() => setIngredientsOpen(true)}
          />
        </div>
        <div className="rr-cook-counter rr-mono" aria-live="polite">
          {total === 0
            ? '00 / 00'
            : `${String(safeStep + 1).padStart(2, '0')} / ${String(total).padStart(2, '0')}`}
        </div>
      </header>

      <main className="rr-cook-stage">
        {total === 0 ? (
          <p className="rr-cook-step rr-cook-step-empty">
            This recipe doesn't have any method steps yet.
          </p>
        ) : (
          <p className="rr-cook-step">{currentText}</p>
        )}
      </main>

      <div
        className="rr-cook-zones"
        role="group"
        aria-label="Step navigation"
      >
        <button
          type="button"
          className="rr-cook-zone rr-cook-zone-prev"
          onClick={goPrev}
          disabled={isFirst}
          aria-label="Previous step"
        >
          <IconChevronLeft size={28} aria-hidden="true" />
          <span className="rr-cook-zone-label">Previous</span>
        </button>
        <button
          type="button"
          className="rr-cook-zone rr-cook-zone-next"
          onClick={goNext}
          disabled={isLast}
          aria-label="Next step"
        >
          <span className="rr-cook-zone-label">{isLast ? 'Last step' : 'Next'}</span>
          <IconChevronRight size={28} aria-hidden="true" />
        </button>
      </div>

      <IngredientDrawer
        open={ingredientsOpen}
        onClose={() => setIngredientsOpen(false)}
        ingredients={recipe.ingredients}
      />
    </div>
  );
};

export default CookMode;
